import { createSignal, createEffect } from 'solid-js';
import { makePersisted } from '@solid-primitives/storage';
import type { User } from '../types';

// Auth store state
interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
}

// Create persisted signals using sessionStorage
// sessionStorage clears when browser closes, forcing re-login
const [token, setToken] = makePersisted(createSignal<string | null>(null), {
  name: 'auth_token',
  storage: sessionStorage, // Use sessionStorage instead of localStorage
});

const [refreshToken, setRefreshToken] = makePersisted(createSignal<string | null>(null), {
  name: 'auth_refresh_token',
  storage: sessionStorage, // Use sessionStorage instead of localStorage
});

const [user, setUser] = makePersisted(createSignal<User | null>(null), {
  name: 'auth_user',
  storage: sessionStorage, // Use sessionStorage instead of localStorage
});

const [isAuthenticated, setIsAuthenticated] = createSignal(false);

// Clean stored tokens that have Bearer prefix (from old login sessions)
const cleanStoredTokens = () => {
  const storedToken = sessionStorage.getItem('auth_token');
  const storedRefreshToken = sessionStorage.getItem('auth_refresh_token');

  if (storedToken && storedToken.startsWith('Bearer ')) {
    sessionStorage.setItem('auth_token', storedToken.slice(7));
  }

  if (storedRefreshToken && storedRefreshToken.startsWith('Bearer ')) {
    sessionStorage.setItem('auth_refresh_token', storedRefreshToken.slice(7));
  }
};

// Run cleanup on initialization
cleanStoredTokens();

// Initialize authentication state on load
createEffect(() => {
  const currentToken = token();
  if (currentToken) {
    // Verify token is valid (basic check - could decode JWT)
    try {
      setIsAuthenticated(true);
    } catch (error) {
      // Token invalid, clear it
      logout();
    }
  } else {
    setIsAuthenticated(false);
  }
});

// Auth actions
export const login = (authToken: string, userData?: User, authRefreshToken?: string) => {
  // Security: Validate token is not empty before storing
  if (!authToken || authToken.trim().length === 0) {
    throw new Error('Invalid token: Token cannot be empty');
  }

  // Strip 'Bearer ' prefix if it's included in the token
  const cleanToken = authToken.startsWith('Bearer ') ? authToken.slice(7) : authToken;

  // Validate cleaned token is not empty
  if (cleanToken.trim().length === 0) {
    throw new Error('Invalid token: Token cannot be empty after cleaning');
  }

  setToken(cleanToken);

  if (authRefreshToken) {
    // Validate refresh token is not empty
    if (authRefreshToken.trim().length > 0) {
      // Also clean refresh token if it has Bearer prefix
      const cleanRefreshToken = authRefreshToken.startsWith('Bearer ') ? authRefreshToken.slice(7) : authRefreshToken;
      if (cleanRefreshToken.trim().length > 0) {
        setRefreshToken(cleanRefreshToken);
      }
    }
  }

  if (userData) {
    setUser(userData);
  }
  setIsAuthenticated(true);
};

export const logout = () => {
  setToken(null);
  setRefreshToken(null);
  setUser(null);
  setIsAuthenticated(false);

  // Clear all sessionStorage auth data
  sessionStorage.removeItem('auth_token');
  sessionStorage.removeItem('auth_refresh_token');
  sessionStorage.removeItem('auth_user');
};

export const updateUser = (userData: User) => {
  setUser(userData);
};

// Selectors
export const getToken = () => token();
export const getRefreshToken = () => refreshToken();
export const getUser = () => user();
export const getIsAuthenticated = () => isAuthenticated();

// Export signals for reactive access
export const authStore = {
  token,
  refreshToken,
  user,
  isAuthenticated,
  login,
  logout,
  updateUser,
  getToken,
  getRefreshToken,
  getUser,
  getIsAuthenticated,
};
